'use client';

import type { FragmentType } from 'src/gql';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@savvy-kit/components/button';
import { Separator } from '@savvy-kit/components/separator';
import { SavedViews } from '@savvy-kit/patterns/saved-views';
import { debounce } from 'es-toolkit';
import { Settings2 } from 'lucide-react';
import { graphql, unmaskFragment } from 'src/gql';

import type { SavedTaxFilter } from './hooks/use-saved-tax-filters';
import type { TaxFilterSavedViewProps } from './tax-filter-dropdown';
import type { TaxOverviewTableColumn } from './tax-overview-table-columns';
import {
  getActiveTaxFilterPresetId,
  setActiveTaxFilterPresetId,
  useSavedTaxFilters,
} from './hooks/use-saved-tax-filters';
import { TaxColumnsDisplay } from './tax-columns-display';
import { TaxFilterDropdown } from './tax-filter-dropdown';
import { TaxOverviewPaginationFooterFragment } from './tax-overview-pagination-footer';
import { ensureRequiredColumns, getDefaultColumns } from './tax-overview-table-columns';
import { TaxOverviewTableList } from './tax-overview-table-list';
import { useTaxOverviewParams } from './tax-overview-url-state';
import { TaxSearchField } from './tax-search-field';
import TaxTableCards from './tax-table-cards';

export const TaxOverviewTableFragment = graphql(/* GraphQL */ `
  fragment TaxOverviewTable on Query {
    taxEnrollmentsTableRows(
      first: $first
      after: $after
      last: $last
      before: $before
      sortField: $sortField
      sortDirection: $sortDirection
      requireAdvisorActionFilter: $requireAdvisorActionFilter
      requireClientActionFilter: $requireClientActionFilter
      waitingOnThirdPartyFilter: $waitingOnThirdPartyFilter
      query: $query
      enrollmentTypeFilter: $enrollmentTypeFilter
      statusFilter: $statusFilter
      taxYearFilter: $taxYearFilter
    ) {
      ...TaxOverviewPaginationFooter
      ...TaxOverviewTableBody
      nodes {
        taxEnrollmentId
      }
    }
  }
`);

type Props = {
  readonly queryFrag: FragmentType<typeof TaxOverviewTableFragment> | undefined;
  readonly loading: boolean;
};

export function TaxOverviewTable({ queryFrag, loading }: Props) {
  const data = unmaskFragment(TaxOverviewTableFragment, queryFrag);
  const { params, setSearchQuery, clearAllFilters, updateParams } = useTaxOverviewParams();

  const setSearchQueryRef = useRef(setSearchQuery);
  setSearchQueryRef.current = setSearchQuery;
  const debouncedSetSearchQuery = useMemo(() => debounce((value: string) => setSearchQueryRef.current(value), 500), []);
  useEffect(() => () => debouncedSetSearchQuery.cancel(), [debouncedSetSearchQuery]);

  const [selectedColumns, setSelectedColumns] = useState<TaxOverviewTableColumn[]>(() =>
    ensureRequiredColumns(getDefaultColumns()),
  );
  const [displayColumnsDialogOpen, setDisplayColumnsDialogOpen] = useState(false);
  const displayButtonRef = useRef<HTMLDivElement>(null);
  const [localSearchQuery, setLocalSearchQuery] = useState(params.searchQuery ?? '');

  useEffect(() => {
    setLocalSearchQuery(params.searchQuery ?? '');
  }, [params.searchQuery]);

  const handleColumnsChange = (newColumns: TaxOverviewTableColumn[]) => {
    setSelectedColumns(newColumns);
  };

  const handleSearch = (value: string) => {
    setLocalSearchQuery(value);
    debouncedSetSearchQuery(value);
  };

  const tableRowsData = data?.taxEnrollmentsTableRows;
  const paginationData = unmaskFragment(TaxOverviewPaginationFooterFragment, tableRowsData);
  const totalCount = paginationData?.totalCount ?? 0;

  const hasActiveFilters =
    (params.enrollmentTypeFilter !== null && params.enrollmentTypeFilter.length > 0) ||
    (params.statusFilter !== null && params.statusFilter.length > 0) ||
    (params.taxYearFilter !== null && params.taxYearFilter.length > 0) ||
    params.requireAdvisorActionFilter === true ||
    params.requireClientActionFilter === true ||
    params.waitingOnThirdPartyFilter === true;

  // Saved views
  const { savedFilters, atLimit, saveFilter, updateFilter, renameFilter, deleteFilter } = useSavedTaxFilters();

  const [activeFilterId, setActiveFilterId] = useState<string | null>(() => {
    const id = getActiveTaxFilterPresetId();
    if (id === null) return null;
    const exists = savedFilters.some((f) => f.id === id);
    if (!exists) {
      setActiveTaxFilterPresetId(null);
      return null;
    }
    return id;
  });

  const currentFilters = useMemo(
    () => ({
      enrollmentTypeFilter: params.enrollmentTypeFilter ?? [],
      statusFilter: params.statusFilter ?? [],
      taxYearFilter: params.taxYearFilter ?? [],
    }),
    [params.enrollmentTypeFilter, params.statusFilter, params.taxYearFilter],
  );

  const isModified = useMemo(() => {
    if (activeFilterId === null) return false;
    const preset = savedFilters.find((f) => f.id === activeFilterId);
    if (preset === undefined) return false;
    const sort = (arr: readonly string[]) => [...arr].sort();
    const f = preset.filters;
    return (
      JSON.stringify(sort(currentFilters.enrollmentTypeFilter)) !== JSON.stringify(sort(f.enrollmentTypeFilter)) ||
      JSON.stringify(sort(currentFilters.statusFilter)) !== JSON.stringify(sort(f.statusFilter)) ||
      JSON.stringify(sort(currentFilters.taxYearFilter)) !== JSON.stringify(sort(f.taxYearFilter))
    );
  }, [activeFilterId, savedFilters, currentFilters]);

  const handleSaveFilter = useCallback(
    (name: string) => {
      const preset = saveFilter(name, currentFilters);
      if (preset !== null) {
        setActiveFilterId(preset.id);
        setActiveTaxFilterPresetId(preset.id);
      }
    },
    [saveFilter, currentFilters],
  );

  const handleUpdateFilter = useCallback(
    (id: string) => {
      updateFilter(id, currentFilters);
      setActiveFilterId(id);
      setActiveTaxFilterPresetId(id);
    },
    [updateFilter, currentFilters],
  );

  const activePresetName = useMemo(
    () => (activeFilterId !== null ? (savedFilters.find((f) => f.id === activeFilterId)?.name ?? null) : null),
    [activeFilterId, savedFilters],
  );

  const handleLoadPreset = useCallback(
    (preset: SavedTaxFilter) => {
      updateParams({
        enrollmentTypeFilter:
          preset.filters.enrollmentTypeFilter.length > 0 ? preset.filters.enrollmentTypeFilter : null,
        statusFilter: preset.filters.statusFilter.length > 0 ? preset.filters.statusFilter : null,
        taxYearFilter: preset.filters.taxYearFilter.length > 0 ? preset.filters.taxYearFilter : null,
      });
      setActiveFilterId(preset.id);
      setActiveTaxFilterPresetId(preset.id);
    },
    [updateParams],
  );

  const handleClearPreset = useCallback(() => {
    clearAllFilters();
    setActiveFilterId(null);
    setActiveTaxFilterPresetId(null);
  }, [clearAllFilters]);

  const savedFilterProps = useMemo<TaxFilterSavedViewProps>(
    () => ({
      atLimit,
      activePresetName,
      isModified,
      hasFilters: hasActiveFilters,
      existingNames: savedFilters.map((f) => f.name),
      savedFilters: savedFilters.map((f) => ({ id: f.id, name: f.name })),
      onSave: handleSaveFilter,
      onUpdate: handleUpdateFilter,
    }),
    [atLimit, activePresetName, isModified, hasActiveFilters, savedFilters, handleSaveFilter, handleUpdateFilter],
  );

  return (
    <div className="flex flex-col gap-8">
      <TaxTableCards />
      <div className="border border-light rounded-md overflow-hidden">
        <div className="px-3 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <TaxSearchField value={localSearchQuery} onSearch={handleSearch} />
            <TaxFilterDropdown savedViews={savedFilterProps} totalCount={totalCount} loading={loading} />
          </div>
          <div ref={displayButtonRef}>
            <Button
              variant="secondary"
              icon={<Settings2 size={16} />}
              onClick={() => setDisplayColumnsDialogOpen(true)}
            >
              Display
            </Button>
          </div>
        </div>
        <TaxColumnsDisplay
          open={displayColumnsDialogOpen}
          onClose={() => setDisplayColumnsDialogOpen(false)}
          selectedColumns={selectedColumns}
          onColumnsChange={handleColumnsChange}
          anchorEl={displayButtonRef.current}
        />

        {savedFilters.length > 0 && (
          <>
            <Separator className="bg-border-light" />
            <div className="px-4 py-2 flex items-center gap-2">
              <SavedViews
                savedViews={savedFilters}
                activeViewId={activeFilterId}
                isModified={isModified}
                onLoadView={handleLoadPreset}
                onClearView={handleClearPreset}
                onRenameView={(id, newName) => renameFilter(id, newName)}
                onDeleteView={(id) => {
                  const remaining = deleteFilter(id);
                  if (activeFilterId === id || remaining.length === 0) {
                    setActiveFilterId(null);
                    setActiveTaxFilterPresetId(null);
                  }
                }}
              />
            </div>
          </>
        )}

        <TaxOverviewTableList
          queryFrag={tableRowsData}
          paginationFrag={tableRowsData}
          loading={loading}
          columns={selectedColumns}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearAllFilters}
          onColumnOrderChange={handleColumnsChange}
        />
      </div>
    </div>
  );
}
